/**
 * Content Auto-Merger
 *
 * Detects when bundled seed data has new items and merges them into
 * localStorage without touching existing user data (SR progress, stats,
 * edits, etc.).
 *
 * How it works:
 * 1. Each seed data file exports a CONTENT_VERSION number
 * 2. On app load, compare stored version with bundled version
 * 3. If bundled is newer: find items in seed that aren't in storage (by ID), add them
 * 4. Update the stored version number
 *
 * This means:
 * - New cards/questions added in code updates appear automatically
 * - User's existing progress, edits, and custom content are never touched
 * - Deleted seed items are NOT removed from user's storage (they keep what they have)
 */

import storageService from '../storage/storageService';

/**
 * Merge new seed content into existing stored data.
 *
 * @param {string} namespace - Storage namespace (e.g., 'flashcards')
 * @param {string} key - Storage key (e.g., 'all')
 * @param {string} versionKey - Key to store content version (e.g., 'content-version')
 * @param {Array} seedData - Full seed data array from the data file
 * @param {number} seedVersion - Current seed data version number
 * @param {Function} setData - React state setter for the data
 * @param {string[]} [updateFields] - Optional list of fields to refresh from seed on
 *   version bumps (e.g., ['attributes']). Only applies to existing seed items
 *   (items where is_custom is falsy). User progress fields are never touched.
 * @param {boolean} [pruneRemoved] - Optional. When true, non-custom items whose
 *   ID is no longer in seed are removed from stored data. Items flagged with
 *   `is_custom: true` are always preserved. Use only for content types that
 *   carry no meaningful per-item user progress (e.g., matrices — drill stats
 *   live in a separate key). Do NOT enable for flashcards / exam / drill where
 *   per-item SR data or stats must be retained even if seed removes the item.
 * @returns {{ merged: number, updated: number, pruned: number, total: number }} Counts
 */
export function autoMergeContent({
  namespace,
  key,
  versionKey = 'content-version',
  seedData,
  seedVersion,
  currentData,
  setData,
  updateFields,
  pruneRemoved = false,
}) {
  const storedVersion = storageService.get(namespace, versionKey, 0);

  // Already up to date
  if (storedVersion >= seedVersion) {
    return { merged: 0, updated: 0, pruned: 0, total: currentData.length };
  }

  // First-time initialization (no data at all)
  if (currentData.length === 0) {
    setData(seedData);
    storageService.set(namespace, versionKey, seedVersion);
    return { merged: seedData.length, updated: 0, pruned: 0, total: seedData.length };
  }

  // Build seed lookup by ID for both merge and update
  const seedById = new Map(seedData.map(item => [item.id, item]));

  // Merge: add items from seed that don't exist in current data (by ID)
  const existingIds = new Set(currentData.map(item => item.id));
  const newItems = seedData.filter(item => !existingIds.has(item.id));

  // Prune: drop non-custom items whose ID is no longer in seed. User-created
  // items (is_custom: true) are always kept.
  let pruned = 0;
  let workingData = currentData;
  if (pruneRemoved) {
    workingData = currentData.filter(item => {
      if (item.is_custom) return true;
      if (seedById.has(item.id)) return true;
      pruned++;
      return false;
    });
  }

  // Update: refresh specified fields on existing non-custom items
  let updated = 0;
  let mergedData = workingData;

  if (updateFields && updateFields.length > 0) {
    mergedData = workingData.map(item => {
      // Skip user-created content
      if (item.is_custom) return item;
      const seedItem = seedById.get(item.id);
      if (!seedItem) return item;

      // Copy only the specified fields from seed
      const patch = {};
      for (const field of updateFields) {
        if (field in seedItem) {
          patch[field] = seedItem[field];
        }
      }
      if (Object.keys(patch).length > 0) {
        updated++;
        return { ...item, ...patch };
      }
      return item;
    });
  }

  if (newItems.length > 0) {
    mergedData = [...mergedData, ...newItems];
  }

  if (newItems.length > 0 || updated > 0 || pruned > 0) {
    setData(mergedData);
  }

  storageService.set(namespace, versionKey, seedVersion);
  return { merged: newItems.length, updated, pruned, total: mergedData.length };
}
